import { Box, Button, H3, Spinner, Stack, Text } from "@adfenix/alexandria";
import { ReadLayer } from "@adfenix/types";
import { Table } from "components/Datatable";
import { ReadLayerContext } from "components/ReadLayer/ReadLayerQueryContext";
import { ReadLayerStateWrapper } from "components/ReadLayer/ReadLayerWrapper";
import { SearchField } from "components/ReadLayer/SearchField";
import { Timestamp } from "components/Timestamp";
import { ReactNode, useContext, useState } from "react";
import { Modal } from "components/Modal";
import { ButtonVariant } from "@adfenix/alexandria/dist/components/Button/common";
import { useNavigate, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { CSSProperties } from "styled-components";
import { ReadLayerApi } from "api/services";
import { BackOfficePage } from "components/Page/BackOfficePage";

interface BaseProps {
  tag?: string[];
  title?: string;
  entities?: string[];
}

interface Props extends BaseProps {
  correlationId: string;
}

interface ModalProps extends Props {
  children: ReactNode;
  buttonVariant?: ButtonVariant;
  buttonStyle?: CSSProperties;
}

const EntityLink = ({ type, id }: { type: string; id: string }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  let link = "";

  if (type === "ProgramInstance") {
    link = `/back-office/program-instance/${id}`;
  }

  if (type === "Property") {
    link = `/back-office/listing/${id}`;
  }

  if (type === "Campaign") {
    link = `/back-office/campaign/${id}`;
  }

  if (type === "Order") {
    link = `/back-office/order/${id}`;
  }

  if (type === "Agent") {
    const findAgentPage = async () => {
      setLoading(true);
      const agent = await ReadLayerApi.readLayer.authV1ReadLayerAgentDetail(id);
      setLoading(false);

      navigate(`/${agent.primaryHqId}/agent-profile/${agent.id}`);
    };

    return (
      <Button
        isLoading={loading}
        onClick={findAgentPage}
        variant="text"
        logLabel="go-to-agent"
      >
        Agent
      </Button>
    );
  }

  if (type === "Office") {
    const findOfficePage = async () => {
      setLoading(true);
      const office = await ReadLayerApi.readLayer.authV1ReadLayerOfficeDetail(
        id
      );
      setLoading(false);

      navigate(
        `/back-office/hq/${office.hq.id}/office/${office.id}/office-settings`
      );
    };

    return (
      <Button
        isLoading={loading}
        onClick={findOfficePage}
        variant="text"
        logLabel="go-to-office"
      >
        Office
      </Button>
    );
  }

  if (!link) return null;

  return <Link to={link}>{type}</Link>;
};

const AuditLogTable = ({ entities }: { entities: string[] }) => {
  const ctx = useContext(ReadLayerContext);

  if (!ctx) return null;

  const log = ctx.data?.documents as ReadLayer.AuditLogSearchModel[];

  return (
    <Table
      progressComponent={
        <Box style={{ minHeight: 200 }}>
          <Spinner centered />
        </Box>
      }
      progressPending={ctx.isLoading || !ctx.updateQuery || !log}
      columns={[
        {
          sortable: true,
          name: "Performed at",
          grow: 0.5,
          selector: "performedAt",
          cell: (row: ReadLayer.AuditLogSearchModel) => (
            <Timestamp bold timestamp={row.performedAt} />
          )
        },
        {
          name: "Performed by",
          selector: "performedBy",
          grow: 0.5,
          cell: (row: ReadLayer.AuditLogSearchModel) => (
            <Text bold>{row.performedBy}</Text>
          )
        },
        {
          name: "Action",
          selector: "action",
          grow: 3,
          cell: (row: ReadLayer.AuditLogSearchModel) => (
            <Text bold style={{ maxWidth: "100%" }}>
              {row.action}
            </Text>
          )
        },
        {
          name: "Links",
          selector: "entities",
          grow: 1,
          cell: (row: ReadLayer.AuditLogSearchModel) => (
            <ul>
              {entities.map(entityType => {
                if (!row.entities) return null;

                const entityIds = row.entities[entityType];

                if (!entityIds) return null;

                return entityIds.map(id => (
                  <li key={id}>
                    <EntityLink type={entityType} id={id} />
                  </li>
                ));
              })}
            </ul>
          )
        }
      ]}
      highlightOnHover
      keyField="id"
      paginationServerOptions={{
        persistSelectedOnPageChange: true,
        persistSelectedOnSort: true
      }}
      paginationServer
      paginationTotalRows={Math.min(
        ctx.data?.totalNumberOfDocuments ?? 0,
        10000
      )}
      sortServer
      onSort={(column, direction) => {
        ctx.updateQuery({
          sortOnColumn: column.selector,
          sortAscending: direction === "asc"
        });
      }}
      onChangePage={page => {
        ctx.updateQuery({ page });
      }}
      onChangeRowsPerPage={pageSize => {
        ctx.updateQuery({ pageSize, page: 1 });
      }}
      paginationRowsPerPageOptions={[10, 15, 20, 25, 30, 50, 75, 100]}
      paginationDefaultPage={ctx.query.page}
      data={log}
    />
  );
};

export const AuditLogReadLayer = ({
  correlationId,
  tag,
  title = "Log",
  entities = []
}: Props) => {
  const filters = [
    {
      column: "correlationId",
      value: correlationId
    }
  ];

  if (tag) {
    tag.forEach(x =>
      filters.push({
        column: "tags",
        value: x
      })
    );
  }

  const searchColumns = ["performedBy", "action"];

  for (const entity of entities) {
    searchColumns.push(`entities.${entity}`);
  }

  return (
    <Box>
      <Stack spacing="l">
        {title && <H3>{title}</H3>}

        <ReadLayerStateWrapper
          indexName="audit-log"
          serviceName="read-layer"
          sortOnColumn="performedAt"
          sortAscending={false}
          filters={filters}
          subscribeToPropUpdates={true}
        >
          <Stack spacing="m" alignItems="center">
            <SearchField
              minWidth={700}
              placeholder="Search by performed by or action"
              columns={searchColumns}
            />

            <AuditLogTable entities={entities} />
          </Stack>
        </ReadLayerStateWrapper>
      </Stack>
    </Box>
  );
};

export const AuditLogReadLayerModal = ({
  correlationId,
  tag,
  title = "Log",
  children,
  buttonVariant = "text",
  entities = [],
  buttonStyle
}: ModalProps) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Modal show={showModal} onClose={() => setShowModal(false)}>
        <Modal.Card $bigCard={true}>
          <Modal.Card.Head onClose={() => setShowModal(false)}>
            <Modal.Card.Title>{title}</Modal.Card.Title>
          </Modal.Card.Head>

          <Modal.Card.Body>
            <AuditLogReadLayer
              correlationId={correlationId}
              title={""}
              tag={tag}
              entities={entities}
            />
          </Modal.Card.Body>

          <Modal.Card.Foot style={{ alignItems: "normal" }}></Modal.Card.Foot>
        </Modal.Card>
      </Modal>
      <Button
        style={buttonStyle}
        logLabel="open-audit-log-modal-button"
        variant={buttonVariant}
        onClick={() => setShowModal(true)}
      >
        {children}
      </Button>
    </>
  );
};

export const AuditLogPage = ({ tag, title = "Log", entities }: BaseProps) => {
  const { correlationId } = useParams();

  if (!correlationId) return null;

  return (
    <BackOfficePage pageSize="full" title={title}>
      <AuditLogReadLayer
        correlationId={correlationId}
        title={""}
        tag={tag}
        entities={entities}
      />
    </BackOfficePage>
  );
};
